// Equipment Manager Component - Simplified and Intuitive
import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit, Trash2, Loader2, Upload, Save, X, GripVertical, Image as ImageIcon, Package, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../ui/button';
import { compressImage } from '../../utils/imageCompression';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function for image upload with compression
const uploadImage = async (file) => {
  const compressedFile = await compressImage(file, { maxWidth: 1200, quality: 0.85 });
  const formData = new FormData();
  formData.append('file', compressedFile);
  const response = await fetch(`${BACKEND_URL}/api/admin/upload`, { 
    method: 'POST', 
    credentials: 'include', 
    body: formData 
  });
  if (response.ok) {
    const data = await response.json();
    return `${BACKEND_URL}${data.url}`;
  }
  throw new Error('Upload failed');
};

// Simple Part Editor - One part at a time
const PartEditor = ({ part, onSave, onCancel, onDelete, isNew = false }) => {
  const [form, setForm] = useState(part);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const imageUrl = await uploadImage(file);
      setForm({ ...form, image: imageUrl });
    } catch (err) { console.error('Upload error:', err); }
    finally { setUploading(false); }
  };

  return (
    <div className="border-2 border-blue-300 bg-blue-50 rounded-xl p-6">
      <h4 className="font-medium text-slate-800 mb-4 flex items-center gap-2">
        <Package className="w-5 h-5 text-blue-600" />
        {isNew ? '➕ Nouvelle Pièce' : '✏️ Modifier la Pièce'}
      </h4>
      
      <div className="flex gap-6">
        {/* Image Upload - Left */}
        <div className="w-48 flex-shrink-0">
          <div className="aspect-square bg-white rounded-xl overflow-hidden border-2 border-dashed border-slate-300 relative mb-3">
            {form.image ? (
              <img src={form.image} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-slate-400 p-4">
                <ImageIcon className="w-12 h-12 mb-2" />
                <span className="text-sm text-center">Cliquez pour ajouter une image</span>
              </div>
            )}
          </div>
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
          <Button 
            type="button" 
            variant="outline" 
            className="w-full" 
            onClick={() => fileInputRef.current?.click()} 
            disabled={uploading}
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
            {form.image ? 'Changer l\'image' : 'Ajouter une image'}
          </Button>
        </div>
        
        {/* Form Fields - Right */}
        <div className="flex-1 space-y-4">
          {/* Names */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Nom de la pièce</label>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <span className="text-xs text-slate-500 mb-1 block">🇫🇷 Français</span>
                <input
                  type="text"
                  value={form.name_fr || ''}
                  onChange={(e) => setForm({ ...form, name_fr: e.target.value })}
                  placeholder="Ex: Courroie de transmission"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                />
              </div>
              <div>
                <span className="text-xs text-slate-500 mb-1 block">🇬🇧 English</span>
                <input
                  type="text"
                  value={form.name_en || ''}
                  onChange={(e) => setForm({ ...form, name_en: e.target.value })}
                  placeholder="Ex: Drive belt"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                />
              </div>
              <div>
                <span className="text-xs text-slate-500 mb-1 block">🇩🇿 العربية</span>
                <input
                  type="text"
                  value={form.name_ar || ''}
                  onChange={(e) => setForm({ ...form, name_ar: e.target.value })}
                  placeholder="مثال: حزام النقل"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-right"
                  dir="rtl"
                />
              </div>
            </div>
          </div>
          
          {/* Descriptions */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <span className="text-xs text-slate-500 mb-1 block">🇫🇷 Français</span>
                <textarea
                  value={form.description_fr || ''}
                  onChange={(e) => setForm({ ...form, description_fr: e.target.value })}
                  placeholder="Description détaillée de la pièce..."
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  rows={3}
                />
              </div>
              <div>
                <span className="text-xs text-slate-500 mb-1 block">🇬🇧 English</span>
                <textarea
                  value={form.description_en || ''}
                  onChange={(e) => setForm({ ...form, description_en: e.target.value })}
                  placeholder="Detailed part description..."
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  rows={3}
                />
              </div>
              <div>
                <span className="text-xs text-slate-500 mb-1 block">🇩🇿 العربية</span>
                <textarea
                  value={form.description_ar || ''}
                  onChange={(e) => setForm({ ...form, description_ar: e.target.value })}
                  placeholder="وصف تفصيلي للقطعة..."
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-right"
                  dir="rtl"
                  rows={3}
                />
              </div>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex justify-between pt-2">
            <div>
              {!isNew && onDelete && (
                <Button type="button" variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={onDelete}>
                  <Trash2 className="w-4 h-4 mr-1" /> Supprimer cette pièce
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={onCancel}>
                Annuler
              </Button>
              <Button type="button" onClick={() => onSave(form)}>
                <Save className="w-4 h-4 mr-2" /> Enregistrer
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Part Card Display with reorder buttons
const PartCard = ({ part, index, totalParts, onEdit, onDelete, onMoveUp, onMoveDown }) => {
  return (
    <div className="flex items-center gap-4 p-4 bg-white rounded-xl border border-slate-200 hover:border-slate-300 transition-colors group">
      {/* Order buttons */}
      <div className="flex flex-col gap-1">
        <button 
          onClick={onMoveUp} 
          disabled={index === 0}
          className="p-1 hover:bg-slate-100 rounded disabled:opacity-30 disabled:cursor-not-allowed"
          title="Monter"
        >
          <ChevronUp className="w-4 h-4 text-slate-500" />
        </button>
        <span className="text-xs text-slate-400 text-center">{index + 1}</span>
        <button 
          onClick={onMoveDown} 
          disabled={index === totalParts - 1}
          className="p-1 hover:bg-slate-100 rounded disabled:opacity-30 disabled:cursor-not-allowed"
          title="Descendre"
        >
          <ChevronDown className="w-4 h-4 text-slate-500" />
        </button>
      </div>

      {/* Image */}
      <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-slate-100">
        {part.image ? (
          <img src={part.image} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-300">
            <ImageIcon className="w-8 h-8" />
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="flex-1 min-w-0">
        <h5 className="font-medium text-slate-800 truncate">
          {part.name_fr || part.name_en || `Pièce ${index + 1}`}
        </h5>
        {part.description_fr && (
          <p className="text-sm text-slate-500 truncate">{part.description_fr}</p>
        )}
        {!part.name_fr && !part.description_fr && (
          <p className="text-sm text-orange-500 italic">⚠️ Pas de nom/description</p>
        )}
      </div>
      
      {/* Actions */}
      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={onEdit} className="p-2 hover:bg-slate-100 rounded-lg" title="Modifier">
          <Edit className="w-4 h-4 text-slate-500" />
        </button>
        <button onClick={onDelete} className="p-2 hover:bg-red-50 rounded-lg" title="Supprimer">
          <Trash2 className="w-4 h-4 text-red-500" />
        </button>
      </div>
    </div>
  );
};

// Category Form (simplified)
const CategoryForm = ({ category, onSave, onCancel }) => {
  const [form, setForm] = useState(category);
  const [editingPartIndex, setEditingPartIndex] = useState(null);
  const [showNewPart, setShowNewPart] = useState(false);
  const [uploadingMainImage, setUploadingMainImage] = useState(false);
  const mainImageRef = useRef(null);

  // Initialize parts from images if needed (runs once on mount)
  useEffect(() => {
    // If we have images but no parts (or empty parts array), convert images to parts
    const hasImages = category.images && category.images.length > 0;
    const hasParts = category.parts && category.parts.length > 0;
    
    if (hasImages && !hasParts) {
      const convertedParts = category.images.map((img, i) => ({
        id: `part_${Date.now()}_${i}`,
        image: img,
        name_en: '', name_fr: '', name_ar: '',
        description_en: '', description_fr: '', description_ar: ''
      }));
      setForm(prevForm => ({ ...prevForm, parts: convertedParts }));
    }
  }, [category]);

  const handleMainImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingMainImage(true);
    try {
      const imageUrl = await uploadImage(file);
      setForm({ ...form, image_url: imageUrl });
    } catch (err) { console.error('Upload error:', err); }
    finally { setUploadingMainImage(false); }
  };

  const handleSavePart = (partData, index) => {
    const newParts = [...(form.parts || [])];
    if (index !== null && index !== undefined) {
      newParts[index] = partData;
    } else {
      newParts.push({ ...partData, id: `part_${Date.now()}` });
    }
    setForm({ ...form, parts: newParts });
    setEditingPartIndex(null);
    setShowNewPart(false);
  };

  const handleDeletePart = (index) => {
    if (!window.confirm('Supprimer cette pièce ?')) return;
    const newParts = form.parts.filter((_, i) => i !== index);
    setForm({ ...form, parts: newParts });
    setEditingPartIndex(null);
  };

  const movePart = (index, direction) => {
    const newParts = [...(form.parts || [])];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newParts.length) return;
    [newParts[index], newParts[newIndex]] = [newParts[newIndex], newParts[index]];
    setForm({ ...form, parts: newParts });
  };

  const handleFinalSave = () => {
    // Sync images for backward compatibility
    const formToSave = {
      ...form,
      images: (form.parts || []).map(p => p.image).filter(Boolean)
    };
    onSave(formToSave);
  };

  return (
    <div className="space-y-6">
      {/* Category Basic Info */}
      <div className="bg-slate-50 rounded-xl p-6">
        <h3 className="font-medium text-slate-800 mb-4">📁 Informations de la catégorie</h3>
        
        {/* Main Image Upload */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-2">📷 Image principale de la catégorie</label>
          <div className="flex items-start gap-4">
            <div className="w-40 h-40 flex-shrink-0 rounded-xl overflow-hidden bg-white border-2 border-dashed border-slate-300">
              {form.image_url ? (
                <img src={form.image_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-slate-400 p-4">
                  <ImageIcon className="w-10 h-10 mb-2" />
                  <span className="text-xs text-center">Aucune image</span>
                </div>
              )}
            </div>
            <div className="flex-1">
              <input ref={mainImageRef} type="file" accept="image/*" onChange={handleMainImageUpload} className="hidden" />
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => mainImageRef.current?.click()} 
                disabled={uploadingMainImage}
                className="mb-2"
              >
                {uploadingMainImage ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                {form.image_url ? 'Changer l\'image' : 'Ajouter une image'}
              </Button>
              <p className="text-xs text-slate-500">
                Cette image sera utilisée comme image principale de la catégorie sur la page Équipements.
              </p>
              {form.image_url && (
                <button 
                  type="button" 
                  onClick={() => setForm({ ...form, image_url: '' })}
                  className="text-xs text-red-500 hover:text-red-700 mt-2"
                >
                  Supprimer l'image
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">ID unique</label>
            <input 
              type="text" 
              value={form.id || ''} 
              onChange={(e) => setForm({...form, id: e.target.value})} 
              placeholder="ex: sampo" 
              disabled={category.id}
              className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-sm ${category.id ? 'bg-slate-100' : ''}`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">🇫🇷 Nom FR</label>
            <input type="text" value={form.name_fr || ''} onChange={(e) => setForm({...form, name_fr: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">🇬🇧 Name EN</label>
            <input type="text" value={form.name_en || ''} onChange={(e) => setForm({...form, name_en: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">🇩🇿 الاسم AR</label>
            <input type="text" value={form.name_ar || ''} onChange={(e) => setForm({...form, name_ar: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-right" dir="rtl" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Description de la catégorie (optionnel)</label>
          <div className="grid grid-cols-3 gap-3">
            <textarea value={form.description_fr || ''} onChange={(e) => setForm({...form, description_fr: e.target.value})} placeholder="🇫🇷 Description FR" className="px-3 py-2 border border-slate-200 rounded-lg text-sm" rows={2} />
            <textarea value={form.description_en || ''} onChange={(e) => setForm({...form, description_en: e.target.value})} placeholder="🇬🇧 Description EN" className="px-3 py-2 border border-slate-200 rounded-lg text-sm" rows={2} />
            <textarea value={form.description_ar || ''} onChange={(e) => setForm({...form, description_ar: e.target.value})} placeholder="🇩🇿 الوصف AR" className="px-3 py-2 border border-slate-200 rounded-lg text-sm text-right" dir="rtl" rows={2} />
          </div>
        </div>
      </div>

      {/* Parts Section */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-medium text-slate-800 flex items-center gap-2">
            <Package className="w-5 h-5" /> 
            Pièces de cette catégorie 
            <span className="text-sm text-slate-400 font-normal">({(form.parts || []).length} pièce{(form.parts || []).length > 1 ? 's' : ''})</span>
          </h3>
          {!showNewPart && editingPartIndex === null && (
            <Button onClick={() => setShowNewPart(true)} size="sm">
              <Plus className="w-4 h-4 mr-2" /> Ajouter une pièce
            </Button>
          )}
        </div>

        {/* New Part Form */}
        {showNewPart && (
          <div className="mb-4">
            <PartEditor
              part={{ image: '', name_fr: '', name_en: '', name_ar: '', description_fr: '', description_en: '', description_ar: '' }}
              onSave={(data) => handleSavePart(data, null)}
              onCancel={() => setShowNewPart(false)}
              isNew={true}
            />
          </div>
        )}

        {/* Editing Existing Part */}
        {editingPartIndex !== null && form.parts && form.parts[editingPartIndex] && (
          <div className="mb-4">
            <PartEditor
              part={form.parts[editingPartIndex]}
              onSave={(data) => handleSavePart(data, editingPartIndex)}
              onCancel={() => setEditingPartIndex(null)}
              onDelete={() => handleDeletePart(editingPartIndex)}
            />
          </div>
        )}

        {/* Parts List */}
        {!showNewPart && editingPartIndex === null && (
          <div className="space-y-2">
            {(form.parts || []).map((part, index) => (
              <PartCard
                key={part.id || index}
                part={part}
                index={index}
                totalParts={(form.parts || []).length}
                onEdit={() => setEditingPartIndex(index)}
                onDelete={() => handleDeletePart(index)}
                onMoveUp={() => movePart(index, -1)}
                onMoveDown={() => movePart(index, 1)}
              />
            ))}
            
            {(!form.parts || form.parts.length === 0) && (
              <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-xl">
                <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 mb-2">Aucune pièce dans cette catégorie</p>
                <Button onClick={() => setShowNewPart(true)} variant="outline" size="sm">
                  <Plus className="w-4 h-4 mr-2" /> Ajouter la première pièce
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Save/Cancel */}
      <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
        <Button variant="ghost" onClick={onCancel}>
          <X className="w-4 h-4 mr-2" /> Annuler
        </Button>
        <Button onClick={handleFinalSave}>
          <Save className="w-4 h-4 mr-2" /> Enregistrer la catégorie
        </Button>
      </div>
    </div>
  );
};

// Main Equipment Manager
const EquipmentManager = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showNewCategory, setShowNewCategory] = useState(false);

  useEffect(() => { fetchCategories(); }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/equipment-categories`, { credentials: 'include' });
      if (response.ok) { 
        const data = await response.json(); 
        setCategories(data || []); 
      }
    } catch (err) { console.error('Error:', err); }
    finally { setLoading(false); }
  };

  const handleSaveCategory = async (categoryData) => {
    const isNew = !editingCategory?.id;
    const url = isNew 
      ? `${BACKEND_URL}/api/admin/equipment-categories`
      : `${BACKEND_URL}/api/admin/equipment-categories/${categoryData.id}`;
    
    try {
      await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(categoryData)
      });
      setEditingCategory(null);
      setShowNewCategory(false);
      fetchCategories();
    } catch (err) { console.error('Error:', err); }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Supprimer cette catégorie et toutes ses pièces ?')) return;
    try {
      await fetch(`${BACKEND_URL}/api/admin/equipment-categories/${id}`, { method: 'DELETE', credentials: 'include' });
      fetchCategories();
    } catch (err) { console.error('Error:', err); }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }

  // Show category form if editing or creating
  if (editingCategory || showNewCategory) {
    return (
      <div>
        <h2 className="text-2xl font-light text-slate-800 mb-6">
          {showNewCategory ? '➕ Nouvelle Catégorie' : `✏️ Modifier: ${editingCategory?.name_fr || editingCategory?.name_en}`}
        </h2>
        <CategoryForm
          category={editingCategory || { id: '', name_fr: '', name_en: '', name_ar: '', description_fr: '', description_en: '', description_ar: '', parts: [] }}
          onSave={handleSaveCategory}
          onCancel={() => { setEditingCategory(null); setShowNewCategory(false); }}
        />
      </div>
    );
  }

  // Categories List
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-light text-slate-800">Équipements</h2>
          <p className="text-sm text-slate-500 mt-1">Gérez vos catégories et leurs pièces</p>
        </div>
        <Button onClick={() => setShowNewCategory(true)}>
          <Plus className="w-4 h-4 mr-2" /> Nouvelle catégorie
        </Button>
      </div>

      <div className="space-y-4">
        {categories.map((cat) => {
          const partsCount = cat.parts?.length || cat.images?.length || 0;
          const hasDescriptions = cat.parts?.some(p => p.name_fr || p.description_fr);
          // Priorité: image_url (principale) > première pièce > legacy images
          const displayImage = cat.image_url || cat.parts?.[0]?.image || cat.images?.[0];
          
          return (
            <div key={cat.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-md transition-shadow">
              <div className="flex">
                {/* Preview Image */}
                <div className="w-48 h-40 flex-shrink-0 bg-slate-100 relative">
                  {displayImage ? (
                    <img src={displayImage} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-400">
                      <Package className="w-12 h-12" />
                    </div>
                  )}
                  {partsCount > 1 && (
                    <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/60 text-white text-xs rounded-full">
                      {partsCount} pièces
                    </div>
                  )}
                  {cat.image_url && (
                    <div className="absolute top-2 left-2 px-2 py-1 bg-blue-500 text-white text-xs rounded-full">
                      Image principale
                    </div>
                  )}
                </div>
                
                {/* Content */}
                <div className="flex-1 p-5">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-xl font-medium text-slate-800">{cat.name_fr || cat.name_en}</h3>
                      {cat.name_en && cat.name_fr && (
                        <p className="text-sm text-slate-500">🇬🇧 {cat.name_en}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setEditingCategory(cat)}>
                        <Edit className="w-4 h-4 mr-1" /> Modifier
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => handleDeleteCategory(cat.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {cat.description_fr && (
                    <p className="text-sm text-slate-600 mt-2 line-clamp-2">{cat.description_fr}</p>
                  )}
                  
                  <div className="mt-4 flex items-center gap-4 text-sm">
                    <span className="text-slate-500">
                      <Package className="w-4 h-4 inline mr-1" />
                      {partsCount} pièce{partsCount > 1 ? 's' : ''}
                    </span>
                    {hasDescriptions ? (
                      <span className="text-green-600">✓ Descriptions ajoutées</span>
                    ) : partsCount > 0 ? (
                      <span className="text-orange-500">⚠️ Descriptions manquantes</span>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {categories.length === 0 && (
        <div className="text-center py-16 bg-slate-50 rounded-xl">
          <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-600 mb-2">Aucune catégorie</h3>
          <p className="text-slate-500 mb-4">Commencez par créer votre première catégorie d'équipements</p>
          <Button onClick={() => setShowNewCategory(true)}>
            <Plus className="w-4 h-4 mr-2" /> Créer une catégorie
          </Button>
        </div>
      )}

      {/* Help Box */}
      <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-xl">
        <h4 className="font-medium text-blue-900 mb-2">💡 Comment ça marche ?</h4>
        <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
          <li><strong>Créez une catégorie</strong> (ex: "Pièces SAMPO", "Pièces CLAAS")</li>
          <li><strong>Ajoutez des pièces</strong> dans chaque catégorie avec leur image</li>
          <li><strong>Remplissez les descriptions</strong> pour chaque pièce (nom + description en 3 langues)</li>
          <li>Les pièces s'afficheront en <strong>liste sur le site</strong> avec photo à gauche et description à droite</li>
        </ol>
      </div>
    </div>
  );
};

export default EquipmentManager;
